/**
 * Карточка машины — блок `WEB-2.4` (`FR-WEB-502`, решения формы `Б3`–`Б11`):
 * данные записи реестра, фото машины, открытые неисправности с фото,
 * комментарием, автором и временем, запись неисправности и полная история.
 *
 * Карточка показывает **ровно ответ** операции карточки и после каждого
 * удавшегося действия перечитывает себя целиком. В «неисправность выявлена»
 * машину переводит сервер, закрывает неисправности переход в «готов» — тоже
 * сервер: состояние и момент закрытия приходят перечитанной карточкой, и ни
 * одного литерала значения состояния здесь нет — подпись собирает строитель.
 *
 * Действия — «Записать неисправность», «Добавить фото», «Удалить» — видны
 * **любому** члену и у выведенной машины тоже: право и вывод решает сервер, а
 * его отказ (`403`, `422`) ложится его словами на месте действия, карточка
 * остаётся. Признака права в членстве для парка контракт не несёт, и
 * выдумывать его клиент не вправе.
 *
 * Клиент не считает фото до шести, не проверяет тип, размер и длину
 * комментария — у полей файла нет даже `accept`: всё это судит сервер. Тело
 * загрузки собирает слой (`multipart`), экран отдаёт ему выбранное как есть.
 * Ключ идемпотентности загрузки рождается при нажатии и живёт, пока запрос в
 * пути (кнопка неактивна); после ответа или правки поля — новый (решение
 * формы `Б9`, форма `WEB-2.1` `Б5`).
 *
 * Фото — изображение с адресом **ровно** из ответа, в порядке ответа: адрес
 * непрозрачен, и ни склейки, ни заголовка входа на нём нет. Срок подписанной
 * ссылки ни с чем не сравнивается: протухшую ссылку карточка узнаёт событием
 * `error` у изображения и перечитывает себя **один** раз на загрузку экрана —
 * без цикла. У фото неисправности кнопки удаления нет: запись неисправности
 * неизменяема, операции нет.
 *
 * Формы неисправности и фото — одни и те же узлы на всё время жизни экрана:
 * отказ и смена языка не пересобирают их, и введённый комментарий с
 * выбранными файлами остаются в самих полях (долг `TD-72`).
 */

import { ApiFailure, newIdempotencyKey } from '../api/client';
import type { ApiClient } from '../api/client';
import type { VehicleCard, VehicleFault, VehiclePhoto } from '../api/contract';
import {
  activeLocale,
  bodyTypeStringKey,
  repairStateStringKey,
  vehicleKindStringKey,
  vehicleStatusStringKey,
} from '../i18n';
import type { Strings } from '../i18n';
import { fleetPath, vehicleServicePath } from '../router';
import {
  createFieldMessageSlot,
  enumLabel,
  momentLabel,
  placeState,
  refusalText,
  renderFieldMessages,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface VehicleCardScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly vehicleId: string;
}

export interface VehicleCardScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  /** Карточка из последнего ответа либо `null` — ответа нет. */
  readonly card: VehicleCard | null;
  load(): Promise<void>;
  /** Переподписать экран на новом языке, не трогая введённого (долг `TD-72`). */
  relabel(next: Strings): void;
}

/** Файлы, выбранные в поле, — все и в порядке выбора: клиент их не отбирает. */
function chosenFiles(field: HTMLInputElement): File[] {
  const chosen: File[] = [];
  const list = field.files;

  if (list === null) {
    return chosen;
  }

  for (let index = 0; index < list.length; index += 1) {
    const file = list.item(index);

    if (file !== null) {
      chosen.push(file);
    }
  }

  return chosen;
}

export function createVehicleCardScreen(options: VehicleCardScreenOptions): VehicleCardScreen {
  const { client, companyId, vehicleId } = options;
  let { strings } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const identity = document.createElement('p');
  const serviceLink = document.createElement('a');
  const fleetLink = document.createElement('a');
  const body = document.createElement('div');

  const photosHeading = document.createElement('h2');
  const photosList = document.createElement('ul');
  const photosEmpty = document.createElement('p');
  const photoForm = document.createElement('form');
  const photoNotice = createFieldMessageSlot();
  const photoCaption = document.createElement('label');
  const photoCaptionText = document.createElement('span');
  const photoField = document.createElement('input');
  const photoMessage = createFieldMessageSlot();
  const photoSubmit = document.createElement('button');

  const faultsHeading = document.createElement('h2');
  const faultsList = document.createElement('ul');
  const faultsEmpty = document.createElement('p');
  const faultForm = document.createElement('form');
  const faultNotice = createFieldMessageSlot();
  const commentCaption = document.createElement('label');
  const commentCaptionText = document.createElement('span');
  const commentField = document.createElement('textarea');
  const commentMessage = createFieldMessageSlot();
  const faultPhotosCaption = document.createElement('label');
  const faultPhotosCaptionText = document.createElement('span');
  const faultPhotosField = document.createElement('input');
  const faultPhotosMessage = createFieldMessageSlot();
  const faultSubmit = document.createElement('button');

  const historyButton = document.createElement('button');
  const historyNotice = createFieldMessageSlot();
  const historyList = document.createElement('ul');
  const historyEmpty = document.createElement('p');

  element.className = 'screen screen-vehicle-card';
  identity.className = 'vehicle-identity';
  serviceLink.className = 'company-link';
  fleetLink.className = 'company-link';
  body.className = 'vehicle-card';
  photosList.className = 'vehicle-photos';
  photosEmpty.className = 'list-empty';
  photoForm.className = 'form';
  photoCaption.className = 'form-field';
  photoCaptionText.className = 'form-field__caption';
  photoSubmit.className = 'action';
  faultsList.className = 'vehicle-faults';
  faultsEmpty.className = 'list-empty';
  faultForm.className = 'form';
  commentCaption.className = 'form-field';
  commentCaptionText.className = 'form-field__caption';
  faultPhotosCaption.className = 'form-field';
  faultPhotosCaptionText.className = 'form-field__caption';
  faultSubmit.className = 'action';
  historyButton.className = 'action';
  historyList.className = 'vehicle-faults';
  historyEmpty.className = 'list-empty';

  // Адреса собирает единственный строитель: карточка и экран сервиса
  // ссылаются друг на друга (решение формы `Б2`), реестр — на карточку.
  serviceLink.href = vehicleServicePath(companyId, vehicleId);
  fleetLink.href = fleetPath(companyId);

  // Имена полей — имена частей встроенных форм контракта: по ним сервер
  // называет место своего сообщения. Атрибута `accept` нет ни у одного поля
  // файла — тип судит сервер по содержимому.
  photoField.type = 'file';
  photoField.name = 'file';
  commentField.name = 'comment';
  faultPhotosField.type = 'file';
  faultPhotosField.name = 'photos';
  faultPhotosField.multiple = true;
  photoSubmit.type = 'submit';
  faultSubmit.type = 'submit';
  historyButton.type = 'button';

  photoCaption.append(photoCaptionText, photoField, photoMessage);
  photoForm.append(photoNotice, photoCaption, photoSubmit);
  commentCaption.append(commentCaptionText, commentField, commentMessage);
  faultPhotosCaption.append(faultPhotosCaptionText, faultPhotosField, faultPhotosMessage);
  faultForm.append(faultNotice, commentCaption, faultPhotosCaption, faultSubmit);

  /** Подписи экрана — одним местом: смена языка переподписывает их на месте. */
  function caption(): void {
    heading.textContent = strings.vehicle_card_title;
    serviceLink.textContent = strings.service_title;
    fleetLink.textContent = strings.fleet_title;
    photosHeading.textContent = strings.vehicle_photo_title;
    photosEmpty.textContent = strings.vehicle_photo_empty;
    photoCaptionText.textContent = strings.vehicle_photo_file;
    photoSubmit.textContent = strings.vehicle_photo_add;
    faultsHeading.textContent = strings.vehicle_fault_title;
    faultsEmpty.textContent = strings.vehicle_fault_empty;
    commentCaptionText.textContent = strings.vehicle_fault_comment;
    faultPhotosCaptionText.textContent = strings.vehicle_fault_photos;
    faultSubmit.textContent = strings.vehicle_fault_add;
    historyButton.textContent = strings.vehicle_fault_history;
    historyEmpty.textContent = strings.vehicle_fault_history_empty;
  }

  caption();

  let state: ScreenState | null = 'loading';
  let card: VehicleCard | null = null;
  /** Пришёл ли ответ сервера — та же величина, что на экране фирмы. */
  let answered = false;
  /** Полная история неисправностей — только после нажатия «Все неисправности». */
  let history: readonly VehicleFault[] | null = null;
  /**
   * Перечитана ли карточка по ошибке изображения в эту загрузку экрана
   * (решение формы `Б6`): одно перечитывание на загрузку, и второе `error` той
   * же загрузки цикла не рождает.
   */
  let imageRetried = false;
  let photoSending = false;
  let photoKey: string | null = null;
  let faultSending = false;
  let faultKey: string | null = null;

  /** Изображение с адресом ответа как есть; протухшая ссылка — одно перечитывание. */
  function image(photo: VehiclePhoto): HTMLImageElement {
    const node = document.createElement('img');

    node.className = 'vehicle-photo__image';
    node.src = photo.url;
    node.alt = strings.vehicle_photo_title;
    node.addEventListener('error', () => {
      if (imageRetried) {
        return;
      }

      imageRetried = true;
      void refresh();
    });

    return node;
  }

  /** Фото машины: изображение, момент добавления и «Удалить» этого фото. */
  function photoItem(photo: VehiclePhoto): HTMLElement {
    const node = document.createElement('li');
    const moment = document.createElement('span');
    const remove = document.createElement('button');

    node.className = 'vehicle-photo';
    moment.textContent = momentLabel(activeLocale(), photo.created_at);
    remove.type = 'button';
    remove.className = 'action';
    remove.textContent = strings.vehicle_photo_delete;
    remove.addEventListener('click', () => {
      void deletePhoto(photo.id);
    });

    node.append(image(photo), moment, remove);

    return node;
  }

  /**
   * Строка неисправности: комментарий, автор, момент записи и её фото — без
   * кнопки удаления. В истории — ещё «закрыта» с моментом или «открыта»;
   * момент закрытия приходит с сервера и клиентом не присваивается.
   */
  function faultRow(fault: VehicleFault, withClosure: boolean): HTMLElement {
    const node = document.createElement('li');
    const comment = document.createElement('p');
    const meta = document.createElement('p');
    const photos = document.createElement('div');

    node.className = 'vehicle-fault';
    comment.className = 'vehicle-fault__comment';
    meta.className = 'vehicle-fault__meta';
    photos.className = 'vehicle-fault__photos';
    comment.textContent = fault.comment;

    const parts = [fault.author ?? strings.vehicle_fault_author_unknown, momentLabel(activeLocale(), fault.created_at)];

    if (withClosure) {
      if (fault.closed_at === null) {
        parts.push(strings.vehicle_fault_open);
      } else {
        parts.push(`${strings.vehicle_fault_closed} ${momentLabel(activeLocale(), fault.closed_at)}`);
      }
    }

    meta.textContent = parts.join(' · ');

    for (const photo of fault.photos) {
      photos.append(image(photo));
    }

    node.append(comment, meta, photos);

    return node;
  }

  function identityLine(shown: VehicleCard): string {
    // Пустой тип кузова — пустое место: поле пришло и законно пусто у тягача.
    const bodyShown = shown.body_type === null ? '' : enumLabel(strings, bodyTypeStringKey(shown.body_type));
    const withdrawnShown =
      shown.withdrawn_at === null ? '' : `${strings.fleet_withdrawn_at} ${momentLabel(activeLocale(), shown.withdrawn_at)}`;

    return [
      enumLabel(strings, vehicleKindStringKey(shown.kind)),
      shown.vin,
      shown.tech_passport_number,
      bodyShown,
      enumLabel(strings, repairStateStringKey(shown.repair_state)),
      enumLabel(strings, vehicleStatusStringKey(shown.status)),
      withdrawnShown,
    ].join(' ');
  }

  function render(): void {
    if (card !== null) {
      identity.textContent = identityLine(card);
      photosList.replaceChildren(...card.photos.map(photoItem));
      faultsList.replaceChildren(...card.open_faults.map((fault) => faultRow(fault, false)));
    }

    const historyShown: HTMLElement[] = [];

    if (history !== null) {
      historyList.replaceChildren(...history.map((fault) => faultRow(fault, true)));
      historyShown.push(history.length === 0 ? historyEmpty : historyList);
    }

    // Пустые фото и пустые неисправности — пришедший ответ: подпись встаёт на
    // место списка, а заголовок, действия и формы остаются.
    body.replaceChildren(
      photosHeading,
      card !== null && card.photos.length === 0 ? photosEmpty : photosList,
      photoForm,
      faultsHeading,
      card !== null && card.open_faults.length === 0 ? faultsEmpty : faultsList,
      faultForm,
      historyButton,
      historyNotice,
      ...historyShown,
    );

    // Правило места состояния — одна функция на все экраны (долг `TD-66`):
    // ответа нет — ни заголовка, ни данных машины, и отказ прав с отсутствием
    // машины дают побайтово один экран (`FR-WEB-006`).
    placeState(element, { state, answered, frame: [heading, identity, serviceLink, fleetLink], list: body }, strings);
  }

  /** Исход чтения карточки — первого и всякого перечитывания. */
  async function read(): Promise<void> {
    try {
      card = await client.getCompanyVehicle(companyId, vehicleId);
      answered = true;
      state = screenStateFor({ kind: 'done', empty: false });
    } catch (error) {
      card = null;
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    card = null;
    answered = false;
    history = null;
    imageRetried = false;
    render();

    await read();
  }

  /**
   * Перечитывание после удавшегося действия и по ошибке изображения: экран не
   * уходит в «загрузку» — данные стоят, пока не пришёл новый ответ.
   */
  async function refresh(): Promise<void> {
    await read();
  }

  /**
   * Отказ **действия** — словами сервера на месте действия, карточка
   * остаётся (решения формы `Б3`, `Б8`): сообщения по полям — под полями,
   * которые сервер назвал; непоказанное и общий отказ — над формой. Отказ без
   * конверта контракта — общей подписью ошибки.
   */
  function actionRefused(form: HTMLElement, notice: HTMLElement, error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const homeless = renderFieldMessages(form, failure?.details ?? []);
    const texts: string[] = [];

    for (const detail of homeless) {
      texts.push(detail.message);
    }

    if (texts.length === 0) {
      texts.push(refusalText(error, strings));
    }

    notice.textContent = texts.join(' ');
  }

  /** Сервер ответил отказом — следующая отправка новая и несёт новый ключ. */
  function serverAnswered(error: unknown): boolean {
    return error instanceof ApiFailure && error.code !== null;
  }

  async function recordFault(): Promise<void> {
    faultSending = true;
    faultSubmit.disabled = true;
    faultNotice.textContent = '';
    renderFieldMessages(faultForm, []);
    faultKey ??= newIdempotencyKey();

    try {
      await client.addVehicleFault(
        companyId,
        vehicleId,
        { comment: commentField.value, photos: chosenFiles(faultPhotosField) },
        faultKey,
      );

      faultKey = null;
      commentField.value = '';
      faultPhotosField.value = '';
      // Перечитывается всё: в «неисправность выявлена» машину переводит
      // сервер, и история, если открыта, читается заново по нажатию.
      history = null;
      await refresh();
    } catch (error) {
      if (serverAnswered(error)) {
        faultKey = null;
      }

      actionRefused(faultForm, faultNotice, error);
    } finally {
      faultSending = false;
      faultSubmit.disabled = false;
    }
  }

  async function addPhoto(): Promise<void> {
    photoSending = true;
    photoSubmit.disabled = true;
    photoNotice.textContent = '';
    renderFieldMessages(photoForm, []);
    photoKey ??= newIdempotencyKey();

    try {
      await client.addVehiclePhoto(companyId, vehicleId, photoField.files?.item(0) ?? null, photoKey);

      photoKey = null;
      photoField.value = '';
      await refresh();
    } catch (error) {
      if (serverAnswered(error)) {
        photoKey = null;
      }

      actionRefused(photoForm, photoNotice, error);
    } finally {
      photoSending = false;
      photoSubmit.disabled = false;
    }
  }

  /**
   * Удаление фото машины — после подтверждения (решение формы `Б10`). `204` и
   * `404` («уже удалено») оба перечитывают карточку; отказ сервера — его
   * словами над формой фото. Без конверта (обрыв связи) перечитывать нечем.
   */
  async function deletePhoto(photoId: string): Promise<void> {
    if (!window.confirm(strings.vehicle_photo_delete_confirm)) {
      return;
    }

    photoNotice.textContent = '';
    renderFieldMessages(photoForm, []);

    try {
      await client.deleteVehiclePhoto(companyId, vehicleId, photoId);
    } catch (error) {
      actionRefused(photoForm, photoNotice, error);

      if (!serverAnswered(error)) {
        return;
      }
    }

    await refresh();
  }

  /** «Все неисправности» — история читается только по нажатию, в порядке ответа. */
  async function showHistory(): Promise<void> {
    historyNotice.textContent = '';

    try {
      history = await client.listVehicleFaults(companyId, vehicleId);
    } catch (error) {
      history = null;
      historyNotice.textContent = refusalText(error, strings);
    }

    render();
  }

  // Правка любого поля — новая отправка: ключ прежней снимается (решение `Б9`).
  faultForm.addEventListener('input', () => {
    faultKey = null;
  });
  faultForm.addEventListener('change', () => {
    faultKey = null;
  });
  photoForm.addEventListener('change', () => {
    photoKey = null;
  });

  faultForm.addEventListener('submit', (event) => {
    event.preventDefault();

    if (faultSending) {
      return;
    }

    void recordFault();
  });

  photoForm.addEventListener('submit', (event) => {
    event.preventDefault();

    if (photoSending) {
      return;
    }

    void addPhoto();
  });

  historyButton.addEventListener('click', () => {
    void showHistory();
  });

  /**
   * Переподписать экран на новом языке (долг `TD-72`): формы остаются теми же
   * узлами с введённым и выбранным, меняются только подписи; данные карточки
   * перерисовываются из уже пришедшего ответа.
   */
  function relabel(next: Strings): void {
    strings = next;
    caption();
    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get card(): VehicleCard | null {
      return card;
    },

    load,
    relabel,
  };
}
